/**
 * spiralOrbit — warp-field preset using the spiral generator.
 *
 * 3-arm spiral with mid-tightness twist, adopted aggressively (low beat
 * threshold, short cooldown, short crossfade) so the field locks-in
 * visibly on every solid kick. Heavy magenta-orange palette to read as
 * "neon arcade".
 */

import { WarpKind, type VisualizerPreset } from '../presetTypes';

export const spiralOrbitPreset: VisualizerPreset = {
  id: 'spiralOrbit',
  name: 'Spiral Orbit',
  seed: {
    kind: 'waveform',
    params: {
      baseY: 0.0,
      gain: 0.5,
      halfThick: 0.013,
      intensity: 0.7,
      color: [1.0, 0.45, 0.7], // hot pink
      edgeSharp: 8.5,
    },
  },
  warp: {
    kind: WarpKind.Field,
    params: {
      baseZoom: 0.012,
      bassZoomGain: 0.045,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0,
      rippleFreq: 0,
      decay: 0.975,
      chroma: 0.0035,
      hueShift: 0.018,
      beatPunch: 0.085,
      centerDriftAmp: 0.04,
      centerDriftRate: 0.16,
    },
  },
  warpField: {
    generator: 'spiral',
    seed: 1.7,
    strength: 0.019,
    centerX: 0.5,
    centerY: 0.5,
    // For spiral: param0 = arm count, param1 = twist tightness.
    param0: 3.0,
    param1: 1.4,
    param2: 0,
    adoption: {
      beatThreshold: 0.45,
      cooldownMs: 900,
      crossfadeMs: 350,
      regenerateOnAdopt: true,
    },
  },
  modulation: [
    {
      target: 'warp.baseZoom',
      source: 'bass',
      transform: 'smoothPulse',
      amount: 0.022,
      smoothingMs: 80,
    },
    {
      target: 'warp.chroma',
      source: 'beatConfidence',
      transform: 'smoothPulse',
      amount: 0.005,
      smoothingMs: 180,
    },
    {
      target: 'seed.intensity',
      source: 'mid',
      transform: 'linear',
      amount: 0.4,
      smoothingMs: 60,
    },
  ],
  transition: { durationMs: 900 },
  present: {
    bloomEnabled: true,
    // Spiral Orbit's highlights are sharp and saturated -- raise the
    // threshold so only the brightest streaks bloom, with a tighter spread
    // so the rays stay readable rather than blowing out the frame.
    bloomThreshold: 1.0,
    bloomKnee: 0.18,
    bloomSpread: 1.0,
    bloomIntensity: 0.65,
    vignette: 0.22,
    grain: 0.02,
  },
};
